import React, { useState, useEffect,useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Button,
  TabContent,
  TabPane,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import userServices from "services/userServices";
import helpers from "utils/helpers";
import constants from "../../../constants";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import { useAuth } from "context/AuthContext";
import UserCard from "../components/UserCard";
import EvidenceUploadCard from "./EvidenceUploadCard";
import ChatIcon from "@mui/icons-material/Chat";
import ViewUploadedID from "modals/ViewUploadedID";
import AssignModeratorModal from "./AssignModeratorModal";
import TicketTimeline from "./TicketTimeline";

const getAuthorName = (u) => {
  if (!u) return "";
  if (typeof u === "string") {
    const trimmed = u.trim();
    if (!trimmed || /^[0-9a-fA-F]{24}$/.test(trimmed)) return "";
    return trimmed;
  }
  if (typeof u !== "object") return "";
  const { firstName, lastName, name, email } = u;
  return [firstName, lastName].filter(Boolean).join(" ") || name || email || "";
};

const resolveTicketAuthor = (ticketData, primaryUser) => {
  return (
    getAuthorName(primaryUser) ||
    getAuthorName(ticketData?.updatedBy) ||
    getAuthorName(ticketData?.assignedTo) ||
    getAuthorName(ticketData?.user) ||
    getAuthorName(ticketData?.userId)
  );
};

const normaliseUser = (raw) => {
  if (!raw) return null;
  return {
    ...raw,
    address: raw.address || raw.homeAddress || null,
    profilePictureUrl: raw.profilePictureUrl || null,
  };
};

const SupportTicketDetailsPage = () => {
  const navigate = useNavigate();
  const { id: ticketId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sourceHint = searchParams.get("source");

  const { t } = useTranslation();
  const { notify } = useNotification();
  const { permission } = useAuth();
  const supportPermission = permission?.permissions?.modules?.supportTickets;

  const [activeTab, setActiveTab] = useState("details");
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);

  const notifyRef = useRef(notify);
  useEffect(() => { notifyRef.current = notify; }, [notify]);

  const [userFiles, setUserFiles] = useState([]);
  const [moderatorFiles, setModeratorFiles] = useState([]);
  const [resetUploadKey, setResetUploadKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [resNotes, setResNotes] = useState("");
  const [isResolutionModalOpen, setIsResolutionModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const [idUrl, setIdUrl] = useState(null);
  const [viewUploadedIDOpen, setViewUploadedIDOpen] = useState(false);
  const closeUploadedID = () => setViewUploadedIDOpen(false);
  const zoomImage = (url) => {
    setIdUrl(url);
    setViewUploadedIDOpen(true);
  };

  const loadTicket = useCallback(async () => {
    try {
      if (sourceHint === "Dispute") {
        const res = await userServices.fetchDisputeDetails(ticketId);
        const dispute = res?.data || res?.dispute || res?.result || res;
 
        if (dispute?._id) {
          setTicketData({
            ...dispute,
            source: "Dispute",
            user: normaliseUser(
              dispute.userId?.firstName ? dispute.userId : dispute.createdBy || dispute.user
            ),
          });
          setUserFiles(dispute.mediaUploads || []);
          setModeratorFiles(dispute.moderatorMediaUploads || []);
        }
        return;
      }
 
      const res = await userServices.fetchSupportRequestDetails(ticketId);
      const ticket = res?.data || res;
      if (ticket && !Array.isArray(ticket) && ticket._id) {
        setTicketData({
          ...ticket,
          source: "SupportRequest",
          user: normaliseUser(
            ticket.userId?.firstName ? ticket.userId : ticket.createdBy || ticket.user
          ),
        });
        setUserFiles(ticket.mediaUploads || []);
        setModeratorFiles(ticket.moderatorMediaUploads || []);
        return;
      }
 
      if (!sourceHint) {
        const disputeRes = await userServices.fetchDisputeDetails(ticketId);
        const dispute = disputeRes?.data || disputeRes?.dispute || disputeRes?.result || disputeRes;
        if (dispute?._id) {
          setTicketData({
            ...dispute,
            source: "Dispute",
            user: normaliseUser(
              dispute.userId?.firstName
                ? dispute.userId
                : dispute.createdBy || dispute.user
            ),
          });
          setUserFiles(dispute.mediaUploads || []);
          setModeratorFiles(dispute.moderatorMediaUploads || []);
        }
      }
    } catch (err) {
      console.error("Failed to load ticket details:", err);
      notifyRef.current("error", "Failed to load ticket details.");
    }
  }, [ticketId, sourceHint]);

  useEffect(() => {
    if (!ticketId) return;
    setLoading(true);
    loadTicket().finally(() => setLoading(false));
  }, [ticketId, sourceHint, loadTicket]);

  useEffect(() => {
    if (ticketData) {
      setResNotes(ticketData.resolutionNotes || "");
    }
  }, [ticketData]);


  const resolveFilename = (file) => {
    if (!file) return "";
    if (typeof file === "string") return file.trim();
    return file.filename || file.name || file.key || file.url || "";
  };

  const getImageUrl = (image, source) => {
    if (typeof image === "string" && image.startsWith("http")) return image;
    const folder =
      (source || ticketData?.source) === "Dispute"
        ? "disputes"
        : "support-requests";
    return `${constants.s3Bucket}/${folder}/${image}`;
  };

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString() : t("common.na", "N/A");

  const openResolutionModal = () => {
    setResNotes(ticketData?.status === "resolved" ? (ticketData?.resolutionNotes || "") : "");
    setIsResolutionModalOpen(true);
  };

  const closeResolutionModal = () => {
    setIsResolutionModalOpen(false);
    notify("error", t("page.support-tickets.toast.resolutionNotesRequired", "Resolution reason is required."));
  };

  const handleResolutionConfirm = async () => {
    if (!resNotes.trim()) {
      notify("error", t("page.support-tickets.toast.resolutionNotesRequired", "Resolution reason is required."));
      return;
    }

    const succeeded = await handleResolveOrUpdate("resolved");
    if (succeeded) {
      setIsResolutionModalOpen(false);
    }
  };

  // Status update
  const handleStatusChange = async (newStatus) => {
    if (newStatus === "resolved") {
      openResolutionModal();
      return;
    }

    await handleResolveOrUpdate(newStatus);
  };

  const handleResolveOrUpdate = async (targetStatus) => {
    if (targetStatus === "resolved" && !resNotes.trim()) {
      notify("error", t("page.support-tickets.toast.resolutionNotesRequired", "Resolution reason is required."));
      return false;
    }
    try {
      setIsStatusUpdating(true);
      const payload = {
        status: targetStatus,
        resolutionNotes: resNotes,
      };
      let response;
      if (ticketData?.source === "Dispute") {
        response = await userServices.updateDisputeRequest(
          ticketData._id,
          payload,
          false
        );
      } else {
        response = await userServices.updateSupportRequest(ticketData._id, payload);
      }

      const resPayload = response?.data || response;
      if (resPayload?.success !== false) {
        setTicketData((prev) => ({
          ...prev,
          status: targetStatus,
          resolutionNotes: resNotes,
          ...(targetStatus === "resolved" && !prev.resolvedAt ? { resolvedAt: new Date().toISOString() } : {}),
        }));
        notify("success", t("page.support-tickets.toast.statusUpdated", "Ticket status updated successfully"));
        return true;
      }

      const errorKey = typeof resPayload?.error === "string" ? resPayload.error : "page.support-tickets.toast.statusUpdateFailed";
      const messageVal = typeof resPayload?.message === "string" ? resPayload.message : t("page.support-tickets.toast.statusUpdateFailed", "Failed to update ticket resolution details.");
      notify("error", t(resPayload?.messageKey || errorKey, messageVal));
      return false;
    } catch (err) {
      console.error("Failed to update status:", err);
      const errPayload = err?.response?.data || {};
      const errorKey = typeof errPayload?.error === "string" ? errPayload.error : "page.support-tickets.toast.statusUpdateFailed";
      const messageVal = typeof errPayload?.message === "string" ? errPayload.message : t("page.support-tickets.toast.statusUpdateFailed", "Failed to update ticket resolution details.");
      notify("error", t(errPayload?.messageKey || errorKey, messageVal));
      return false;
    } finally {
      setIsStatusUpdating(false);
    }
  };

  // Moderator evidence upload 
  const handleEvidenceUpload = async (files) => {
  if (!files?.length) return;
  setUploading(true);

  const formData = new FormData();
  files.forEach((file) => formData.append("moderatorMediaUploads", file));

  try {
    if (ticketData?.source === "Dispute") {
      await userServices.updateDisputeRequest(ticketData._id, formData);
    } else {
      await userServices.updateSupportRequest(ticketData._id, formData);
    }

    await loadTicket();

    setResetUploadKey((prev) => prev + 1);
    notify("success", t("page.support-tickets.toast.evidenceUploaded", "Evidence uploaded successfully"));

  } catch (err) {
    console.error(err);
    notify("error", t("page.support-tickets.toast.uploadFailed", "Failed to upload evidence"));
  } finally {
    setUploading(false);
  }
};

  const goToConversation = () => {
    if (!ticketData?._id || !ticketData?.source) return;
    let type = ticketData.source.toLowerCase();
    if (type === "supportrequest") type = "support-ticket";
    if (type === "dispute") type = "dispute";

    navigate(`/admin/conversation/${type}/${ticketData._id}`);
  };

  if (loading)
  return (
    <div className="center-loader">
      <div className="center-loader-content">
        <Spinner />
        <p className="mt-3 text-muted">
          {t("common.loading", "Loading....")}
        </p>
      </div>
    </div>
  );

  if (!ticketData)
    return (
      <div className="text-center py-5">
        <p className="text-muted">{t("page.support-tickets.notFound", "Ticket not found.")}</p>
        <Button color="secondary" onClick={() => navigate(-1)}>
          {t("common.back", "Back")}
        </Button>
      </div>
    );

  const currentStatus = ticketData?.status || "pending";
  const statusClass = helpers.getStatusClass(currentStatus, "ticket");

  let categoryVal = "";
  let descVal = ticketData.description || "";

  if (ticketData.description && ticketData.description.trim().startsWith("Category:")) {
    const lines = ticketData.description.split("\n");
    if (lines.length > 0) {
      categoryVal = lines[0].replace("Category:", "").trim();
      descVal = lines.slice(1).join("\n").trim();
    }
  } else if (ticketData.category) {
    categoryVal = ticketData.category;
  }

  const renderFilesLocal = (files = [], source, isUser = false) => {
    const validFiles = files.filter((f) => resolveFilename(f));

    if (!validFiles.length)
      return (
        <div className="text-center py-5 text-sm text-muted font-weight-normal empty-evidence-msg">
          {isUser
            ? t("page.support-tickets.noUserEvidence", "No evidence submitted by the user yet.")
            : t("page.support-tickets.noModeratorEvidence", "No evidence submitted yet.")}
        </div>
      );

    return (
      <div className="mt-2 d-flex flex-wrap">
        {validFiles.map((file, idx) => {
          const filename = resolveFilename(file);
          const url = getImageUrl(filename, source);
          const isImage = /\.(jpeg|jpg|png|gif|webp|bmp|svg|avif)$/i.test(
            filename
          );
          const shortName =
            filename.length > 20
              ? `${filename.slice(0, 14)}…${filename.slice(-6)}`
              : filename;

          if (!isImage) {
            return (
              <a
                key={idx}
                href={url}
                target="_blank"
                rel="noreferrer"
                className="text-sm d-flex align-items-center mr-3 mb-3 p-2 border rounded bg-light evidence-file-link"
              >
                📄 {shortName}
              </a>
            );
          }

          return (
            <img
              key={idx}
              src={url}
              alt={filename}
              className="rounded shadow-sm cursor-pointer mr-3 mb-3 evidence-img"
              onClick={() => zoomImage(url)}
            />
          );
        })}
      </div>
    );
  };

  const getLinkedInfoContext = () => {
    if (ticketData.source === "Dispute") {
      const order = ticketData?.orderid;
      if (!order) return null;
      
      const fromName = order?.sender_listing_id?.fromPlace?.name || order?.traveler_listing_id?.fromPlace?.name || "N/A";
      const toName = order?.sender_listing_id?.toPlace?.name || order?.traveler_listing_id?.toPlace?.name || "N/A";
      const route = `${fromName} → ${toName}`;

      const sender = order?.senderId
        ? `${order.senderId.firstName} ${order.senderId.lastName}`
        : order?.sender_listing_id?.userId
          ? `${order.sender_listing_id.userId.firstName} ${order.sender_listing_id.userId.lastName}`
          : "N/A";

      const traveler = order?.travelerId
        ? `${order.travelerId.firstName} ${order.travelerId.lastName}`
        : order?.traveler_listing_id?.userId
          ? `${order.traveler_listing_id.userId.firstName} ${order.traveler_listing_id.userId.lastName}`
          : "N/A";

      const reward = (() => {
        const details = order?.sender_listing_id?.packageDetails || order?.traveler_listing_id?.baggageDetails;
        if (!details?.price && !details?.reward) return "-";
        const symbol = details?.currency?.symbol?.trim() || "$";
        return `${symbol}${details.price || details.reward}`;
      })();

      const status = order?.status || "N/A";
      const dateVal = order?.sender_listing_id?.date || order?.traveler_listing_id?.date;
      const date = dateVal ? helpers.getFormattedDate(dateVal, t) : "N/A";

      return { isDispute: true, route, sender, traveler, reward, status, date };
    } else {
      const listing = ticketData?.orderDetails;
      if (!listing) return null;

      const fromName = listing?.fromPlace?.name || "N/A";
      const toName = listing?.toPlace?.name || "N/A";
      const route = `${fromName} → ${toName}`;

      const matchedOrder = listing?.matchedOrder;
      
      let sender = "N/A";
      let traveler = "N/A";

      if (matchedOrder) {
        if (matchedOrder.senderId) {
          sender = `${matchedOrder.senderId.firstName} ${matchedOrder.senderId.lastName}`;
        }
        if (matchedOrder.travelerId) {
          traveler = `${matchedOrder.travelerId.firstName} ${matchedOrder.travelerId.lastName}`;
        }
      } else {
        const isSender = ticketData?.orderType === "senderListing";
        const ownerName = listing?.userId
          ? `${listing.userId.firstName || ""} ${listing.userId.lastName || ""}`.trim()
          : (ticketData?.user ? `${ticketData.user.firstName} ${ticketData.user.lastName}` : "N/A");
        
        if (isSender) {
          sender = ownerName;
        } else {
          traveler = ownerName;
        }
      }

      const reward = listing?.packageDetails?.price
        ? `${listing.packageDetails.currency?.symbol || "$"} ${listing.packageDetails.price}`
        : listing?.baggageDetails?.reward
          ? `${listing.baggageDetails.currency?.symbol || "$"} ${listing.baggageDetails.reward}`
          : "N/A";

      const status = listing?.status || "N/A";
      const date = listing?.date ? helpers.getFormattedDate(listing.date, t) : "N/A";

      return { isDispute: false, route, sender, traveler, reward, status, date };
    }
  };

  return (
    <Container fluid className="user-details-wrapper mt-4">
      <Row>
        <Col lg="8">
          
          {/* ── Header card (title + category + status dropdown + back button) ── */}
          <Card className="shadow border-0 mb-3">
            <CardBody className="p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <small className="text-muted d-block font-weight-bold mb-1 ticket-header-subtitle">
                    {ticketData?.source === "Dispute" 
                      ? t("page.support-tickets.disputeDetails", "Dispute details") 
                      : t("page.support-tickets.requestDetails", "Support request details")}
                  </small>
                  <h3 className="mb-0 font-weight-bold text-dark mt-0 ticket-header-title">
                    {ticketData?.title || (ticketData?.category ? helpers.getCategoryLabel(ticketData.category, t) : "") || "N/A"}
                  </h3>
                </div>

                <div className="d-flex align-items-center gap-12">
                  {supportPermission?.update ? (
                    <UncontrolledDropdown>
                      <DropdownToggle
                        caret
                        size="sm"
                        disabled={currentStatus === "resolved" || isStatusUpdating}
                        className={`status-dropdown ${statusClass}`}
                      >
                        {t(`page.support-tickets.status.${currentStatus}`)}
                      </DropdownToggle>
                      <DropdownMenu right>
                        {Object.keys(helpers.TICKET_STATUSES).map((status) => (
                          <DropdownItem
                            key={status}
                            disabled={status === currentStatus}
                            onClick={() => handleStatusChange(status)}
                          >
                            {t(`page.support-tickets.status.${status}`)}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  ) : (
                    <span className={`status-dropdown ${statusClass}`}>
                      {t(`page.support-tickets.status.${currentStatus}`)}
                    </span>
                  )}

                  <Button
                    color="secondary"
                    onClick={() => navigate(-1)}
                    className="back-btn"
                  >
                    {t("page.support-tickets.contactForm.form.backButton", "Back")}
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* ── Main content card ── */}
          <Card className="shadow border-0 mb-3">
            <CardBody className="p-4">
              
              {/* Tab Navigation */}
              <div className="d-flex border-bottom mb-4 gap-24">
                {[
                  {
                    id: "details",
                    label: t("page.support-tickets.ticketDetailsTab", "Ticket details"),
                  },
                  {
                    id: "evidence",
                    label: t("page.support-tickets.evidence", "Evidence"),
                  },
                ].map((tab) => (
                  <div
                    key={tab.id}
                    className={`cursor-pointer pb-2 custom-tab ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </div>
                ))}
              </div>
 
            <TabContent activeTab={activeTab}>
 
              {/* ════ DETAILS TAB ════ */}
              <TabPane tabId="details">
                  
                  {/* LINKED INFORMATION */}
                  {(() => {
                    const linkedInfo = getLinkedInfoContext();
                    if (!linkedInfo) return null;
                    return (
                      <div className="mb-4 linked-info-box">
                        <div className="linked-info-title">
                          {linkedInfo.isDispute 
                            ? t("page.support-tickets.disputeInformation", "DISPUTE INFORMATION (DELIVERY LINKED)")
                            : t("page.support-tickets.linkedInformation", "LINKED LISTING INFORMATION")}
                        </div>
                        <Row className="gap-16-0">
                          <Col md="6">
                            <span style={{ fontSize: "11px", color: "#5d6d7e", display: "block", marginBottom: "4px", fontWeight: "500" }}>
                              {t("page.messages.order.from", "From")}
                            </span>
                            <span style={{ fontSize: "14px", fontWeight: "600", color: "#2c3e50" }}>
                              {linkedInfo.route.split("→")[0]?.trim() || t("common.na", "N/A")}
                            </span>
                          </Col>
                          <Col md="6">
                            <span className="linked-info-label">
                              {t("page.messages.order.to", "To")}
                            </span>
                            <span className="linked-info-value">
                              {linkedInfo.route.split("→")[1]?.trim() || t("common.na", "N/A")}
                            </span>
                          </Col>

                          <Col md="6">
                            <span className="linked-info-label">
                              {t("page.orders.journeyDate", "Journey date")}
                            </span>
                            <span className="linked-info-value">
                              {linkedInfo.date}
                            </span>
                          </Col>
                          <Col md="6">
                            <span className="linked-info-label">
                              {t("page.messages.order.price", "Price")}
                            </span>
                            <span className="linked-info-value">
                              {linkedInfo.reward}
                            </span>
                          </Col>

                          <Col md="6">
                            <span className="linked-info-label">
                              {t("page.orders.sender", "Sender")}
                            </span>
                            <span className="linked-info-value">
                              {linkedInfo.sender}
                            </span>
                          </Col>
                          <Col md="6">
                            <span className="linked-info-label">
                              {t("page.orders.traveler", "Traveler")}
                            </span>
                            <span className="linked-info-value">
                              {linkedInfo.traveler}
                            </span>
                          </Col>
                        </Row>
                      </div>
                    );
                  })()}

                  {/* CATEGORY */}
                  <div className="mb-4">
                    <div className="section-label">
                      {t("page.support-tickets.contactForm.form.categoryLabel", "Category")}
                    </div>
                    <div className="section-content-box">
                      {helpers.getCategoryLabel(categoryVal, t) || t("common.na", "N/A")}
                    </div>
                  </div>

                  {/* DESCRIPTION */}
                  <div className="mb-4">
                    <div className="section-label">
                      {t("page.messages.support.description", "Description")}
                    </div>
                    <div className="section-content-box">
                      {descVal || "-"}
                    </div>
                  </div>

                  {/* RESOLUTION NOTES (Resolved tickets only) */}
                  {(ticketData?.status === "resolved" && ticketData?.resolutionNotes) && (
                    <div className="mb-4">
                      <div className="section-label d-flex justify-content-between align-items-center">
                        <span>{t("page.support-tickets.resolutionNotes", "Resolution Notes")}</span>
                        {(() => {
                          const author = resolveTicketAuthor(ticketData, ticketData?.resolvedBy);
                          return author ? <span className="text-muted font-weight-normal text-transform-none">{t("page.support-tickets.by", "by")} {author}</span> : null;
                        })()}
                      </div>
                      <div className="section-content-box whitespace-pre-wrap">
                        {ticketData.resolutionNotes}
                      </div>
                    </div>
                  )}

                  {/* REOPEN REASON (Active / Reopened tickets only) */}
                  {(ticketData?.status !== "resolved" && ticketData?.reopenReason) && (
                    <div className="mb-4">
                      <div className="section-label d-flex justify-content-between align-items-center">
                        <span>{t("page.support-tickets.reopenReason", "Reopen Reason")}</span>
                        {(() => {
                          const author = resolveTicketAuthor(ticketData, ticketData?.reopenedBy);
                          return author ? <span className="text-muted font-weight-normal text-transform-none">{t("page.support-tickets.by", "by")} {author}</span> : null;
                        })()}
                      </div>
                      <div className="section-content-box whitespace-pre-wrap">
                        {ticketData.reopenReason}
                      </div>
                    </div>
                  )}

                  <div className="ticket-footer-dates">
                    <div>{t("table.headers.createdAt", "Created Date")}: {formatDate(ticketData.createdAt)}</div>
                    <div>{t("table.headers.updatedAt", "Updated Date")}: {formatDate(ticketData.updatedAt)}</div>
                    {(() => {
                      const updater = ticketData.updatedBy;
                      const lastHistory = !updater && ticketData.statusHistory?.length
                        ? ticketData.statusHistory[ticketData.statusHistory.length - 1]
                        : null;
                      const lastHistoryUser = lastHistory?.changedBy;

                      const getDisplayName = (user) => {
                        if (!user) return "";
                        const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
                        if (name) return user.email ? `${name} (${user.email})` : name;
                        return user.email || user.username || "";
                      };

                      const displayName = updater ? getDisplayName(updater) : getDisplayName(lastHistoryUser);
                      if (!displayName) return null;

                      return (
                        <div>{t("page.support-tickets.lastUpdatedBy", "Last updated by")}: {displayName}</div>
                      );
                    })()}
                  </div>
                </TabPane>

                {/* ════ EVIDENCE TAB ════ */}
                <TabPane tabId="evidence">
                  <Row className="mb-4">
                    <Col lg="6" className="mb-3 mb-lg-0">
                      <Card className="shadow-sm border-0 h-100 rounded-8">
                        <CardBody className="p-4">
                          <div className="text-sm font-weight-bold mb-3 text-dark-gray">
                            {t("page.support-tickets.userEvidence", "User evidence")}
                          </div>
                          {renderFilesLocal(userFiles, ticketData?.source, true)}
                        </CardBody>
                      </Card>
                    </Col>
                    <Col lg="6">
                      <Card className="shadow-sm border-0 h-100 rounded-8">
                        <CardBody className="p-4">
                          <div className="text-sm font-weight-bold mb-3 text-dark-gray">
                            {t("page.support-tickets.moderatorEvidence", "Moderator evidence")}
                          </div>
                          {renderFilesLocal(moderatorFiles, ticketData?.source, false)}
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  {supportPermission?.update && (
                    <div>
                      <div className="linked-info-title">
                        {t("page.support-tickets.uploadEvidence", "UPLOAD EVIDENCE")}
                      </div>
                      <Card className="shadow-sm border-0 mt-2" style={{ borderRadius: "8px" }}>
                        <EvidenceUploadCard
                          key={resetUploadKey}
                          title={""}
                          loading={uploading}
                          onUpload={handleEvidenceUpload}
                        />
                      </Card>
                    </div>
                  )}
                </TabPane>

              </TabContent>
            </CardBody>
          </Card>
        </Col>

        {/* ── Right sidebar ── */}
        <Col lg="4">
          {ticketData?.user && (
            <UserCard
              user={ticketData.user}
              title={t("page.support-tickets.ticketCreatorProfile", "Ticket Creator Profile")}
            />
          )}

          {/* Assigned Moderator Card */}
          <Card 
            className="shadow-sm border-0 mb-3 overflow-hidden action-card-compact cursor-pointer"
            onClick={() => setIsAssignModalOpen(true)}
          >
            <CardBody className="p-3">
              <div className="d-flex align-items-center">
                <div className="icon-container-circle bg-info-soft me-3">
                  <i className="ni ni-badge text-info font-size-22"></i>
                </div>
                <div className="flex-grow-1 pr-2 ml-2">
                  <h6 className="mb-0 font-weight-bold text-dark font-size-sm text-lh-sm">
                    {t("table.headers.assignedModerator", "Assigned Moderator")}
                  </h6>
                  <small className="text-muted d-block mt-1 font-size-xs text-lh-sm">
                    {ticketData.assignedTo ? `${ticketData.assignedTo.firstName} ${ticketData.assignedTo.lastName}` : t("page.support-tickets.unassigned", "Unassigned")}
                  </small>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Join Conversation Card */}
          <Card className="shadow-sm border-0 mb-3 overflow-hidden action-card-compact" onClick={goToConversation}>
            <CardBody className="p-3">
              <div className="d-flex align-items-center">
                <div className="icon-container-circle bg-info-soft me-3">
                  <ChatIcon className="text-info font-size-22" />
                </div>
                <div className="flex-grow-1 pr-2 ml-2">
                  <h6 className="mb-0 font-weight-bold text-dark font-size-sm text-lh-sm">
                    {t("page.messages.joinConversation", "Join Conversation")}
                  </h6>
                  <small className="text-muted d-block mt-1 font-size-xs text-lh-sm">
                    {t("page.messages.conversationSubtext", "Chat with the user regarding this ticket.")}
                  </small>
                </div>
                <div className="text-info ml-auto">
                  <Button color="link" className="p-0 border-0 text-info">
                     <i className="ni ni-bold-right font-size-12rem" />
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Ticket Timeline Card */}
          <Card className="shadow-sm border-0 mb-3">
            <CardBody className="p-4">
              <div className="d-flex align-items-center pb-3 mb-4 ticket-timeline-card-header-wrapper">
                <i className="ni ni-bullet-list-67 ticket-timeline-card-icon mr-2" />
                <div>
                  <h5 className="mb-0 ticket-timeline-card-title">{t("page.support-tickets.timeline.cardTitle", "Ticket status")}</h5>
                  <small className="ticket-timeline-card-subtitle">{t("page.support-tickets.timeline.cardSubtitle", "Track the progress of this ticket")}</small>
                </div>
              </div>
              <TicketTimeline ticketData={ticketData} />
            </CardBody>
          </Card>
        </Col>

      </Row>
      <Modal isOpen={isResolutionModalOpen} toggle={closeResolutionModal} centered size="md">
        <ModalHeader toggle={closeResolutionModal}>
          {t("page.support-tickets.resolutionNotes", "Resolution Notes")}
        </ModalHeader>
        <ModalBody>
          <div className="alert alert-info py-2 px-3 mb-3  mt--4 border-0 d-flex align-items-center" style={{ backgroundColor: "#EFF6FF", color: "#1D4ED8", borderRadius: "8px", fontSize: "12px" }}>
            <span>
              {t("page.support-tickets.resolutionNotesNotice", "Please document the final outcome, reasoning, and resolution steps. These notes will be saved as the official record for this ticket.")}
            </span>
          </div>
          <div className="mb-3">
            <label className="form-control-label font-weight-bold mb-2 d-block">
              {t("page.support-tickets.resolutionNotes", "Resolution Notes")}
            </label>
            <textarea
              className="form-control"
              rows={6}
              value={resNotes}
              onChange={(e) => setResNotes(e.target.value)}
              disabled={isStatusUpdating}
              placeholder={t("page.support-tickets.resolutionPlaceholder", "Document the reasoning, evidence assessed, and resolution steps taken here...")}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeResolutionModal} disabled={isStatusUpdating}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button className="bg-warning text-white" onClick={handleResolutionConfirm} disabled={isStatusUpdating}>
            {isStatusUpdating ? (
              <>
                <Spinner size="sm" className="mr-2" /> {t("common.confirm", "Confirm")}
              </>
            ) : (
              t("common.confirm", "Confirm")
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <ViewUploadedID
        idUrl={idUrl}
        isOpen={viewUploadedIDOpen}
        toggle={closeUploadedID}
      />
      <Modal
        isOpen={isAssignModalOpen}
        toggle={() => setIsAssignModalOpen(false)}
        backdrop="static"
        className="modal-dialog-centered"
      >
        {isAssignModalOpen && (
          <AssignModeratorModal
            ticket={ticketData}
            onClose={() => setIsAssignModalOpen(false)}
            onAssigned={() => {
              setIsAssignModalOpen(false);
              loadTicket();
            }}
          />
        )}
      </Modal>
    </Container>
  );
};

export default SupportTicketDetailsPage;